import { motion, type Variants } from 'framer-motion';

interface CoreValue {
  num: string;
  title: string;
  description: string;
  accent: string;
}

const values: CoreValue[] = [
  {
    num: '01',
    title: 'INNOVATION',
    description: "We push the boundaries of what's possible with GenAI, constantly exploring new frontiers.",
    accent: '#3b82f6',
  },
  {
    num: '02',
    title: 'SECURITY',
    description: 'We develop robust cybersecurity solutions that protect against evolving digital threats.',
    accent: '#3b82f6',
  },
  {
    num: '03',
    title: 'EXCELLENCE',
    description: 'We strive for excellence in every project, delivering solutions that exceed expectations.',
    accent: '#3b82f6',
  },
  {
    num: '04',
    title: 'TRUST',
    description: 'We build trust through transparent practices and reliable security safeguards.',
    accent: '#3b82f6',
  },
];

const containerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

export default function CoreValuesSection() {
  return (
    <section className="relative w-full bg-[#030712] py-24 overflow-hidden">
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center justify-center gap-2.5 mb-4">
            <span className="h-px w-8 bg-[#3b82f6]/70" />
            <span className="text-xs font-bold tracking-[0.28em] text-[#3b82f6] uppercase">
              Core Values
            </span>
            <span className="h-px w-8 bg-[#3b82f6]/70" />
          </div>

          <h2 className="text-4xl sm:text-5xl font-black leading-tight tracking-tight text-white">
            The Pillars of Our{' '}
            <span className="text-[#3b82f6]">
              Integrity
            </span>
          </h2>

          <p className="mt-5 max-w-2xl mx-auto text-slate-300/70 text-base sm:text-lg font-light leading-relaxed">
            Guiding principles that drive our research, development, and community impact.
          </p>
        </motion.div>

        {/* Cards grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
        >
          {values.map((v) => (
            <motion.div
              key={v.num}
              variants={cardVariants}
              className="group relative flex flex-col rounded-2xl border backdrop-blur-md overflow-hidden cursor-default transition-all duration-300 hover:-translate-y-1"
              style={{
                backgroundColor: '#0c0e12',
                borderColor: '#1f2937',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.borderColor = v.accent;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.borderColor = '#1f2937';
              }}
            >
              <div className="relative z-10 p-6 sm:p-7 flex flex-col h-full">
                {/* Number */}
                <span
                  className="text-sm font-black tracking-[0.1em] mb-4"
                  style={{ color: v.accent }}
                >
                  {v.num}
                </span>

                {/* Title */}
                <h3 className="text-base sm:text-lg font-bold text-white uppercase tracking-wider mb-3">
                  {v.title}
                </h3>

                {/* Description */}
                <p className="text-slate-400/80 text-xs sm:text-sm leading-relaxed font-light mt-auto">
                  {v.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
